import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, getProducts, getSettings, createInvoice, downloadInvoicePdf } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import PaymentTermsField from '../components/PaymentTermsField';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  CalendarIcon,
  Loader2,
  Save,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const CreateInvoicePage = () => {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState('');
  const [manualClient, setManualClient] = useState(false);
  const [manualClientName, setManualClientName] = useState('');
  const [manualClientAddress, setManualClientAddress] = useState('');
  const [manualClientNpa, setManualClientNpa] = useState('');
  const [manualClientCity, setManualClientCity] = useState('');
  const [manualClientPhone, setManualClientPhone] = useState('');
  const [manualClientEmail, setManualClientEmail] = useState('');
  const [currency, setCurrency] = useState('CHF');
  const [vatEnabled, setVatEnabled] = useState(true);
  const [status, setStatus] = useState('draft');
  const [dueDate, setDueDate] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(new Date());  // Date de facture (rétroactive possible)
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentMethods, setPaymentMethods] = useState(['virement']);
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [items, setItems] = useState([
    { description: '', quantity: 1, unit_price: 0, vat_rate: 7.7, discount_percent: 0 }
  ]);
  const navigate = useNavigate();

  // Date minimum pour facture rétroactive (1 an en arrière)
  const minInvoiceDate = new Date();
  minInvoiceDate.setFullYear(minInvoiceDate.getFullYear() - 1);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, productsRes, settingsRes] = await Promise.all([
        getClients(),
        getProducts(),
        getSettings()
      ]);
      setClients(clientsRes.data);
      setProducts(productsRes.data);
      setSettings(settingsRes.data);
      setCurrency(settingsRes.data.default_currency || 'CHF');
      setVatEnabled(settingsRes.data.vat_enabled !== false);
      if (settingsRes.data.payment_terms) {
        setPaymentTerms(settingsRes.data.payment_terms);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { 
      description: '', 
      quantity: 1, 
      unit_price: 0, 
      vat_rate: settings?.default_vat_rate || 7.7, 
      discount_percent: 0 
    }]);
  };

  const addProductItem = (product) => {
    setItems([...items, {
      description: product.name + (product.description ? ` - ${product.description}` : ''),
      quantity: 1,
      unit_price: product.unit_price,
      vat_rate: product.vat_rate,
      discount_percent: 0,
      product_id: product.id
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    if (field === 'description') {
      newItems[index][field] = value;
    } else {
      newItems[index][field] = parseFloat(value) || 0;
    }
    setItems(newItems);
  };

  // vatEnabled is now a state variable that can be toggled per invoice

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateDiscount = () => {
    return items.reduce((sum, item) => {
      const lineSubtotal = item.quantity * item.unit_price;
      return sum + (lineSubtotal * (item.discount_percent / 100));
    }, 0);
  };

  const calculateVat = () => {
    if (!vatEnabled) return 0;
    return items.reduce((sum, item) => {
      const lineSubtotal = item.quantity * item.unit_price;
      const afterDiscount = lineSubtotal - (lineSubtotal * (item.discount_percent / 100));
      return sum + (afterDiscount * item.vat_rate / 100);
    }, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount() + calculateVat();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!manualClient && !clientId) {
      toast.error('Veuillez selectionner un client ou saisir manuellement');
      return;
    }
    if (manualClient && !manualClientName.trim()) {
      toast.error('Veuillez saisir le nom du client');
      return;
    }

    if (items.some(item => !item.description || item.quantity <= 0)) {
      toast.error('Veuillez remplir tous les articles');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        client_id: manualClient ? null : clientId,
        manual: manualClient || undefined,
        client_name: manualClient ? manualClientName : undefined,
        client_address: manualClient ? manualClientAddress : undefined,
        client_npa: manualClient ? manualClientNpa : undefined,
        client_city: manualClient ? manualClientCity : undefined,
        client_phone: manualClient ? manualClientPhone : undefined,
        client_email: manualClient ? manualClientEmail : undefined,
        items,
        currency,
        vat_enabled: vatEnabled,
        status,
        payment_terms: paymentTerms || null,
        accepted_payment_methods: paymentMethods.length > 0 ? paymentMethods : null,
        notes: notes || null,
        internal_notes: internalNotes || null,
        due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
        invoice_date: invoiceDate ? format(invoiceDate, 'yyyy-MM-dd') : null
      };
      const response = await createInvoice(payload);
      toast.success(`Facture ${response.data.invoice_number} créée`);
      navigate('/app/invoices');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="create-invoice-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/invoices')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Nouvelle facture
          </h1>
          <p className="text-slate-500">
            Créez une nouvelle facture pour un client
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Client & Currency & VAT */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Client, Devise & TVA</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Client *</Label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={manualClient} onChange={e => { setManualClient(e.target.checked); if (e.target.checked) setClientId(''); }} className="rounded" data-testid="manual-client-toggle" />
                        <span className="text-xs text-slate-500">Saisie manuelle</span>
                      </label>
                    </div>
                    {!manualClient ? (
                      clients.length > 0 ? (
                        <Select value={clientId} onValueChange={(v) => {
                          setClientId(v);
                          const selectedClient = clients.find(c => c.id === v);
                          if (selectedClient?.default_currency) setCurrency(selectedClient.default_currency);
                        }}>
                          <SelectTrigger data-testid="client-select">
                            <SelectValue placeholder="Selectionner un client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.name} - {client.city}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Button type="button" variant="outline" onClick={() => navigate('/clients')}>
                          <Plus className="w-4 h-4 mr-2" />
                          Ajouter un client
                        </Button>
                      )
                    ) : (
                      <div className="space-y-2">
                        <Input value={manualClientName} onChange={e => setManualClientName(e.target.value)}
                          placeholder="Nom / Entreprise" required data-testid="manual-client-name-input" />
                        <Input value={manualClientAddress} onChange={e => setManualClientAddress(e.target.value)}
                          placeholder="Rue et numéro" data-testid="manual-client-address-input" />
                        <div className="grid grid-cols-3 gap-2">
                          <Input value={manualClientNpa} onChange={e => setManualClientNpa(e.target.value)}
                            placeholder="NPA" data-testid="manual-client-npa-input" className="col-span-1" />
                          <Input value={manualClientCity} onChange={e => setManualClientCity(e.target.value)}
                            placeholder="Ville" data-testid="manual-client-city-input" className="col-span-2" />
                        </div>
                        <Input value={manualClientPhone} onChange={e => setManualClientPhone(e.target.value)}
                          placeholder="Téléphone (optionnel)" type="tel" data-testid="manual-client-phone-input" />
                        <Input value={manualClientEmail} onChange={e => setManualClientEmail(e.target.value)}
                          placeholder="Email (optionnel)" type="email" data-testid="manual-client-email-input" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Devise</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger data-testid="currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CHF">CHF - Franc suisse</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="USD">USD - Dollar américain</SelectItem>
                        <SelectItem value="GBP">GBP - Livre sterling</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* TVA Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <p className="font-medium text-slate-900">Avec TVA</p>
                    <p className="text-sm text-slate-500">Cochez pour inclure la TVA sur cette facture</p>
                  </div>
                  <Switch 
                    checked={vatEnabled} 
                    onCheckedChange={setVatEnabled}
                    data-testid="vat-toggle"
                  />
                </div>
                
                {!vatEnabled && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Sans TVA</strong> - La mention "Non assujetti à la TVA" apparaîtra sur la facture PDF.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Items */}
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">Articles</CardTitle>
                <div className="flex gap-2">
                  {products.length > 0 && (
                    <Select onValueChange={(productId) => {
                      const product = products.find(p => p.id === productId);
                      if (product) addProductItem(product);
                    }}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="+ Depuis catalogue" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name} ({formatCurrency(product.unit_price)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ligne vide
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div 
                      key={index} 
                      className="grid grid-cols-12 gap-3 items-start p-4 bg-slate-50 rounded-lg"
                      data-testid={`invoice-item-${index}`}
                    >
                      <div className="col-span-12 sm:col-span-4">
                        <Label className="text-xs text-slate-500">Description</Label>
                        <Input
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Description"
                          data-testid={`item-description-${index}`}
                        />
                      </div>
                      <div className="col-span-3 sm:col-span-1">
                        <Label className="text-xs text-slate-500">Qté</Label>
                        <Input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          data-testid={`item-quantity-${index}`}
                        />
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        <Label className="text-xs text-slate-500">Prix unit.</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          data-testid={`item-price-${index}`}
                        />
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        <Label className="text-xs text-slate-500">Remise %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={item.discount_percent}
                          onChange={(e) => updateItem(index, 'discount_percent', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          data-testid={`item-discount-${index}`}
                        />
                      </div>
                      {vatEnabled && (
                        <div className="col-span-2 sm:col-span-2">
                          <Label className="text-xs text-slate-500">TVA %</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={item.vat_rate}
                            onChange={(e) => updateItem(index, 'vat_rate', e.target.value)}
                            data-testid={`item-vat-${index}`}
                          />
                        </div>
                      )}
                      <div className="col-span-1 flex items-end pb-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Notes (visibles sur la facture)</Label>
                  <Textarea
                    placeholder="Notes pour le client..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    data-testid="notes-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes internes (non visibles)</Label>
                  <Textarea
                    placeholder="Notes internes..."
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    rows={2}
                    data-testid="internal-notes-input"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Statut</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="sent">Envoyée</SelectItem>
                    <SelectItem value="paid">Payée</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Due Date */}
            {/* Date de facture (rétroactive possible) */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Date de facture</CardTitle>
              </CardHeader>
              <CardContent>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="invoice-date-btn">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {invoiceDate ? format(invoiceDate, 'dd MMMM yyyy', { locale: fr }) : 'Aujourd\'hui'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={invoiceDate}
                      onSelect={setInvoiceDate}
                      disabled={(date) => date > new Date() || date < minInvoiceDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500 mt-2">Rétroactif max. 1 an</p>
              </CardContent>
            </Card>

            {/* Due Date */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Échéance</CardTitle>
              </CardHeader>
              <CardContent>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="due-date-btn">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {dueDate ? format(dueDate, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </CardContent>
            </Card>

            {/* Payment Terms */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Conditions de paiement</CardTitle>
              </CardHeader>
              <CardContent>
                <PaymentTermsField
                  value={paymentTerms}
                  onChange={setPaymentTerms}
                  methods={paymentMethods}
                  onMethodsChange={setPaymentMethods}
                />
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="border-slate-200 bg-slate-50">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Sous-total HT</span>
                  <span className="font-medium" data-testid="subtotal">{formatCurrency(calculateSubtotal())}</span>
                </div>
                {calculateDiscount() > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Remises</span>
                    <span className="font-medium text-red-600">-{formatCurrency(calculateDiscount())}</span>
                  </div>
                )}
                {vatEnabled && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">TVA</span>
                    <span className="font-medium" data-testid="vat-total">{formatCurrency(calculateVat())}</span>
                  </div>
                )}
                {!vatEnabled && (
                  <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    Non assujetti à la TVA
                  </div>
                )}
                <div className="border-t border-slate-200 pt-3 flex justify-between">
                  <span className="font-semibold text-slate-900">Total {vatEnabled ? 'TTC' : ''}</span>
                  <span className="font-bold text-lg text-blue-600" data-testid="total">{formatCurrency(calculateTotal())}</span>
                </div>
              </CardContent>
            </Card>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={saving || (!manualClient && !clientId) || (manualClient && !manualClientName.trim())}
              data-testid="save-invoice-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Créer la facture
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateInvoicePage;
